import axios, { AxiosRequestConfig } from 'axios';
import { Thesaurus, CreateThesaurusRequest, UpdateThesaurusRequest, CombineThesaurusRequest } from '../../types/api';

const baseUrl = '/api/thesaurus';

// Fonction pour créer la configuration avec authentification
const createAuthConfig = (token?: string): AxiosRequestConfig => {
  if (!token) return {};
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'accesstoken': token,
      'Content-Type': 'application/json'
    }
  };
};

// Variable pour stocker le token actuel (sera mis à jour par le slice)
let currentToken: string | null = null;

// Fonction pour définir le token (appelée par le slice avant les requêtes)
export const setAuthToken = (token: string | null) => {
  currentToken = token;
};

export const thesaurusService = {
  // Récupérer tous les thésaurus
  getAll: async (params?: { page?: number; limit?: number }): Promise<{ data: Thesaurus[]; pagination: any }> => {
    console.log('🔍 thesaurusService.getAll - token présent:', !!currentToken);
    const response = await axios.get(baseUrl, { 
      params,
      ...createAuthConfig(currentToken || undefined)
    });
    console.log('📥 thesaurusService.getAll - réponse:', response.data.data?.length, 'thésaurus', response.data.data?.map((t: any) => t.nom));
    return response.data;
  },

  // Récupérer un thésaurus par ID
  getById: async (id: string, includeDetails = false): Promise<Thesaurus> => {
    const response = await axios.get(`${baseUrl}/${id}`, {
      params: { includeDetails: includeDetails.toString() },
      ...createAuthConfig(currentToken || undefined)
    });
    return response.data.data;
  },

  // Rechercher des thésaurus
  search: async (query: string): Promise<Thesaurus[]> => {
    const response = await axios.get(baseUrl, { 
      params: { q: query },
      ...createAuthConfig(currentToken || undefined)
    });
    return response.data.data;
  },

  // Créer un nouveau thésaurus
  create: async (data: CreateThesaurusRequest): Promise<Thesaurus> => {
    const response = await axios.post(baseUrl, data, createAuthConfig(currentToken || undefined));
    return response.data.data;
  },

  // Mettre à jour un thésaurus
  update: async (id: string, data: UpdateThesaurusRequest): Promise<Thesaurus> => {
    const response = await axios.put(`${baseUrl}/${id}`, data, createAuthConfig(currentToken || undefined));
    return response.data.data;
  },

  // Supprimer un thésaurus
  delete: async (id: string): Promise<void> => {
    await axios.delete(`${baseUrl}/${id}`, createAuthConfig(currentToken || undefined));
  },

  // Ajouter des entrées à un thésaurus
  addEntries: async (id: string, entreeIds: string[]): Promise<Thesaurus> => {
    const response = await axios.post(`${baseUrl}/${id}`, {
      action: 'addEntries',
      entreeIds
    }, createAuthConfig(currentToken || undefined));
    return response.data.data;
  },

  // Retirer des entrées d'un thésaurus
  removeEntries: async (id: string, entreeIds: string[]): Promise<Thesaurus> => {
    const response = await axios.post(`${baseUrl}/${id}`, {
      action: 'removeEntries',
      entreeIds
    }, createAuthConfig(currentToken || undefined));
    return response.data.data;
  },

  // Combiner plusieurs thésaurus
  combine: async (data: CombineThesaurusRequest): Promise<Thesaurus> => {
    const response = await axios.post(baseUrl, {
      action: 'combine',
      ...data
    }, createAuthConfig(currentToken || undefined));
    return response.data.data;
  },

  // Dégrouper un thésaurus composite lié en thésaurus simple
  unlink: async (id: string): Promise<Thesaurus> => {
    const response = await axios.post(`${baseUrl}/${id}`, {
      action: 'unlink'
    }, createAuthConfig(currentToken || undefined));
    return response.data.data;
  },

  // Ancienne méthode pour compatibilité (déprécié)
  thesaurus: async (): Promise<Thesaurus[]> => {
    console.log('🔍 thesaurusService.thesaurus (old) - token présent:', !!currentToken);
    const response = await axios.get(baseUrl, createAuthConfig(currentToken || undefined));
    console.log('📥 thesaurusService.thesaurus (old) - réponse:', response.data.data?.length, 'thésaurus', response.data.data?.map((t: any) => t.nom));
    return response.data.data || response.data;
  },

  // Ancienne méthode pour compatibilité (déprécié)
  save: async (thesaurus: Partial<Thesaurus>): Promise<Thesaurus> => {
    if (thesaurus._id) {
      const response = await axios.put(`${baseUrl}/${thesaurus._id}`, thesaurus, createAuthConfig(currentToken || undefined));
      return response.data.data;
    } else {
      const response = await axios.post(baseUrl, thesaurus, createAuthConfig(currentToken || undefined));
      return response.data.data;
    }
  },
};
